<script setup lang="ts">
import type { SharingOption } from '.';

import { capitalize } from '@/utils';
import { useEvents } from '~/composables/events/useEvents';
import { createSharingUrlsForUrl } from '~~/utils';
import { h } from 'vue';

import CopyIcon from '~icons/mdi/content-copy';
import EmailIcon from '~icons/mdi/email-outline';
import TwitterIcon from '~icons/ri/twitter-x-fill';
import WhatsAppIcon from '~icons/ri/whatsapp-fill';
import FacebookIcon from '~icons/uil/facebook-f';

import { CopyToastDescription } from '../CopyToastDescription';
import { useToast } from '../ui/toast';

defineOptions({ name: 'SharingContent' });

const emit = defineEmits<{
	(e: 'share-clicked', option: SharingOption): void;
}>();

const { toast } = useToast();
const { trackSharePropertyClicked } = useEvents();

type SharingContentProps = {
	mailSubject: string;
	contentToShare: string;
	showLabels?: boolean;
	variant?: 'default' | 'dropdown';
};

const props = withDefaults(defineProps<SharingContentProps>(), {
	showLabels: false,
	variant: 'default'
});

const modelValue = defineModel<boolean>({ required: true });

const sharingOptions = computed<SharingOption[]>(() => {
	const urls = createSharingUrlsForUrl(props.contentToShare, props.mailSubject);

	return [
		{
			value: urls.whatsapp,
			label: 'WhatsApp',
			icon: WhatsAppIcon
		},
		{
			value: urls.facebook,
			label: 'Facebook',
			icon: FacebookIcon
		},
		{
			value: urls.twitter,
			label: 'Twitter',
			icon: TwitterIcon
		},
		{
			value: urls.email,
			label: 'E-mail',
			icon: EmailIcon
		}
	];
});

function handleCopyLink() {
	navigator?.clipboard?.writeText(props.contentToShare);
	modelValue.value = false;
	toast({
		description: h(CopyToastDescription)
	});

	trackSharePropertyClicked({
		share_whatsapp: false,
		share_facebook: false,
		copy_link: true,
		share_email: false,
		share_twitter: false
	});
}
</script>

<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
	<div :data-variant="variant" class="font-matter-sq font-normal">
		<NuxtLink
			v-for="option in sharingOptions"
			:key="option.label"
			:to="option.value"
			external
			rel="nofollow external"
			target="_blank"
			class="share-icon-link"
			:aria-label="`Compartilhe no ${capitalize(option.label)}`"
			@click="emit('share-clicked', option)"
		>
			<Component :is="option.icon" class="fill-primary text-primary" />
			<span v-if="showLabels" class="text-primary">
				{{ option.label }}
			</span>
		</NuxtLink>
		<button class="share-icon-link" @click="handleCopyLink()">
			<CopyIcon class="fill-primary text-primary" />
			<span v-if="showLabels" class="text-primary"> Copiar link </span>
		</button>
		<slot></slot>
	</div>
</template>
<!-- Styles moved to app/assets/css/main.css to avoid render-blocking CSS -->
